<template>
  <section id="property-types" class="rk-types">
    <div class="rk-container">
      <div class="rk-section__head">
        <div class="rk-kicker">
          <span class="rk-kicker__rule"></span>
          <span class="rk-kicker__text"><i class="bi bi-compass"></i> Built for how you actually manage</span>
        </div>
       <h2>One platform, every kind of property</h2>
      </div>

      <div class="rk-types__body" v-reveal>
        <div class="rk-types__tabs" role="tablist">
          <button
            v-for="t in types"
            :key="t.id"
            class="rk-types__tab"
            :class="{ 'is-active': active === t.id }"
            @click="active = t.id"
            role="tab"
          >
            <i :class="t.icon"></i>
            <span>{{ t.label }}</span>
          </button>
        </div>

        <transition name="rk-fade" mode="out-in">
          <div class="rk-types__panel" :key="active">
            <div class="rk-types__panel-icon"><i :class="current.icon"></i></div>
            <div>
              <h3>{{ current.label }}</h3>
              <p>{{ current.desc }}</p>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed } from 'vue'

const types = [
  { id: 'residential', label: 'Residential', icon: 'bi bi-house-door', desc: 'Apartments and single units — tenant records, monthly billing, and reminders that run themselves.' },
  { id: 'commercial', label: 'Commercial', icon: 'bi bi-building', desc: 'Shops and offices with varied lease terms, service charges, and multiple tenants per building.' },
  { id: 'student', label: 'Student Housing', icon: 'bi bi-mortarboard', desc: 'Higher turnover, shorter leases, and semester-based billing cycles, handled without extra spreadsheets.' },
  { id: 'estates', label: 'Estates & Committees', icon: 'bi bi-diagram-3', desc: 'Shared service charges, resident communication, and transparent records for committees and residents alike.' },
]

const active = ref('residential')
const current = computed(() => types.find((t) => t.id === active.value))
</script>

<style scoped>
.rk-container { max-width: 1000px; margin: 0 auto; padding: 0 1.5rem; }
.rk-types { padding: 6.5rem 0; background: var(--paper); }


.rk-kicker {
  display: inline-flex; align-items: center; gap: 0.85rem;
  margin-bottom: 1rem;
}
.rk-kicker__rule { width: 32px; height: 3px; background: var(--brass); border-radius: 2px; flex-shrink: 0; }
.rk-kicker__text {
  display: inline-flex; align-items: center; gap: 0.5rem;
  font-family: var(--font-mono); font-size: 0.95rem; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase; color: var(--brass);
}
.rk-kicker__text i { font-size: 1rem; }

.rk-section__head h2 {
  font-family: var(--font-display);
  font-weight: 400;
  font-size: 2.6rem;
  letter-spacing: -0.01em;
  color: var(--ink);
  line-height: 1.15;
}

.rk-section__head {
  max-width: 640px;
  margin-bottom: 80px;
}


.rk-types__body {
  background: var(--neu-surface); border-radius: var(--radius-lg);
  box-shadow: var(--neu-raised); padding: 2.5rem; max-width: 780px; margin: 0 auto;
}

.rk-types__tabs { display: flex; gap: 0.6rem; flex-wrap: wrap; margin-bottom: 2rem; }
.rk-types__tab {
  display: inline-flex; align-items: center; gap: 0.5rem;
  font-family: var(--font-body); font-weight: 600; font-size: 0.88rem;
  background: var(--neu-surface); color: var(--slate);
  border: none; border-radius: 100px; padding: 0.65rem 1.1rem; cursor: pointer;
  box-shadow: var(--neu-raised-sm); transition: all 0.25s var(--ease);
}
.rk-types__tab i { color: var(--brass); }
.rk-types__tab.is-active { color: var(--paper); background: var(--ink); box-shadow: var(--neu-inset); }
.rk-types__tab.is-active i { color: var(--brass-light); }

.rk-types__panel { display: flex; gap: 1.5rem; align-items: flex-start; min-height: 110px; }
.rk-types__panel-icon {
  width: 56px; height: 56px; flex-shrink: 0; border-radius: var(--radius-md);
  background: var(--neu-surface); box-shadow: var(--neu-inset);
  display: flex; align-items: center; justify-content: center; color: var(--brass); font-size: 1.5rem;
}
.rk-types__panel h3 { font-family: var(--font-body); font-weight: 700; font-size: 1.15rem; color: var(--ink); margin: 0 0 0.5rem; }
.rk-types__panel p { font-family: var(--font-body); font-size: 0.95rem; color: var(--slate); line-height: 1.65; margin: 0; }

.rk-fade-enter-active, .rk-fade-leave-active { transition: opacity 0.25s var(--ease), transform 0.25s var(--ease); }
.rk-fade-enter-from { opacity: 0; transform: translateY(8px); }
.rk-fade-leave-to { opacity: 0; transform: translateY(-8px); }

@media (max-width: 560px) {
  .rk-types { padding: 4.5rem 0; }
  .rk-types__head h2 { font-size: 1.9rem; }
  .rk-types__body { padding: 1.75rem; }
  .rk-types__panel { flex-direction: column; }
}
</style>